
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { BeltRank } from '@/types';

interface BeltBadgeProps {
    rank: string | BeltRank;
    stripes?: number;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    showStripes?: boolean;
    className?: string;
}

export const BeltBadge: React.FC<BeltBadgeProps> = ({
    rank = 'White',
    stripes = 0,
    size = 'md',
    showStripes = true,
    className
}) => {
    const { t } = useTranslation();
    // Normalization
    const normalizedRank = rank.toString();
    const isBlackBelt = normalizedRank.toLowerCase() === 'black';

    // Size Mappings
    const sizeClasses = {
        sm: 'h-4 w-16 text-[9px]',
        md: 'h-6 w-24 text-[10px]',
        lg: 'h-8 w-32 text-xs',
        xl: 'h-10 w-40 text-sm'
    }[size];

    const patchSizeClasses = {
        sm: 'w-4',
        md: 'w-6',
        lg: 'w-8',
        xl: 'w-10'
    }[size];

    // Helper: Get Main Background Color
    const getBeltColor = (r: string) => {
        // Normalize: replace spaces with hyphens for consistent checking
        const lower = r.toLowerCase().replace(/\s+/g, '-');
        if (lower.includes('black')) {
            if (lower === 'grey-black' || lower === 'gray-black') return 'bg-gray-400';
            if (lower === 'yellow-black') return 'bg-yellow-400';
            if (lower === 'orange-black') return 'bg-orange-500';
            if (lower === 'green-black') return 'bg-green-600';
            return 'bg-slate-900'; // Black belt
        }
        if (lower.includes('brown')) return 'bg-orange-900'; // Brown is often distinct
        if (lower.includes('purple')) return 'bg-purple-600';
        if (lower.includes('blue')) return 'bg-blue-600';
        if (lower.includes('green')) return 'bg-green-600';
        if (lower.includes('orange')) return 'bg-orange-500';
        if (lower.includes('yellow')) return 'bg-yellow-400';
        if (lower.includes('gray') || lower.includes('grey')) return 'bg-gray-400';
        return 'bg-white border border-gray-200'; // Default White
    };

    // Helper: Check for Mixed Bars
    // Kids Belts: White -> Grey-White -> Grey -> Grey-Black -> ...

    let mainBg = getBeltColor(normalizedRank);
    let centerStripeColor: string | null = null;

    // Normalize: replace spaces with hyphens for consistent checking
    const lowerRank = normalizedRank.toLowerCase().replace(/\s+/g, '-');

    if ((lowerRank.includes('-white') || lowerRank.endsWith(' white')) && !lowerRank.startsWith('white')) {
        centerStripeColor = 'bg-white';
        // Ensure main bg is correct
        if (lowerRank.includes('gray') || lowerRank.includes('grey')) mainBg = 'bg-gray-400';
        if (lowerRank.includes('yellow')) mainBg = 'bg-yellow-400';
        if (lowerRank.includes('orange')) mainBg = 'bg-orange-500';
        if (lowerRank.includes('green')) mainBg = 'bg-green-600';
    } else if ((lowerRank.includes('-black') || lowerRank.endsWith(' black')) && !lowerRank.startsWith('black')) {
        centerStripeColor = 'bg-slate-900/90'; // Slightly softer black
        if (lowerRank.includes('gray') || lowerRank.includes('grey')) mainBg = 'bg-gray-400';
        if (lowerRank.includes('yellow')) mainBg = 'bg-yellow-400';
        if (lowerRank.includes('orange')) mainBg = 'bg-orange-500';
        if (lowerRank.includes('green')) mainBg = 'bg-green-600';
    } else if (lowerRank === 'white') {
        mainBg = 'bg-white border border-gray-300';
    }

    // Patch Color: Red for Black Belt, Black for others
    const patchColor = isBlackBelt ? 'bg-red-600' : 'bg-slate-900';

    // Accessible text alternative — the belt is rendered purely with colored
    // divs (WCAG 1.1.1). A non-visual user gets the rank (+ stripes if shown).
    const ariaLabel = (showStripes && stripes > 0)
        ? t('belt.aria_with_stripes', { rank: normalizedRank, count: stripes, defaultValue: `${normalizedRank} belt, ${stripes} stripes` })
        : t('belt.aria_label', { rank: normalizedRank, defaultValue: `${normalizedRank} belt` });

    return (
        <div
            role="img"
            aria-label={ariaLabel}
            className={cn(
                "relative flex items-center shadow-sm overflow-hidden rounded-[1px] select-none",
                sizeClasses,
                mainBg,
                className
            )}
            title={`${normalizedRank} Belt${stripes > 0 ? ` (${stripes} stripes)` : ''} `}
        >
            {/* Center Bar for Mixed Belts */}
            {centerStripeColor && (
                <div aria-hidden="true" className={cn("absolute inset-x-0 h-[30%] top-[35%]", centerStripeColor)} />
            )}

            {/* Content Container - purely for spacing if we add text later, but usually just graphic */}
            <div aria-hidden="true" className="flex-1"></div>

            {/* The Patch (Right Side usually, to hold stripes) */}
            <div aria-hidden="true" className={cn(
                "h-full relative flex items-center justify-evenly px-0.5",
                patchColor,
                patchSizeClasses,
                // Inset the patch from the right edge to show belt color
                size === 'sm' ? 'mr-1.5' : size === 'md' ? 'mr-2' : size === 'lg' ? 'mr-2.5' : 'mr-3'
            )}>
                {showStripes && stripes > 0 && Array.from({ length: stripes }).map((_, i) => (
                    <div
                        key={i}
                        className="h-[80%] w-[2px] bg-white rounded-[1px] shadow-sm transform"
                        style={{ opacity: 0.9 }}
                    />
                ))}
            </div>
        </div>
    );
};
